import { useQueryClient } from '@tanstack/react-query'
import { useBookingSubscription } from './useBookingSubscription'
import type { BookingTable } from '@okeys/shared-utils'

export interface UseBookingSyncOptions {
  /** ID du shop pour s'abonner aux changements de ses bookings */
  shopId: string
  /** Activer/désactiver la synchronisation */
  enabled?: boolean
}

/**
 * Hook qui synchronise automatiquement les bookings via AppSync subscriptions
 * et invalide le cache React Query pour déclencher un refetch.
 *
 * Utilisation:
 * ```tsx
 * // Dans votre composant
 * const { booking } = useBookingQuery() // Query REST existante
 * useBookingSync({ shopId: shopData.shopId }) // Sync en temps réel
 * ```
 */
export function useBookingSync({ shopId, enabled = true }: UseBookingSyncOptions) {
  const queryClient = useQueryClient()

  const { lastBooking, isConnected, error } = useBookingSubscription({
    type: 'shop',
    id: shopId,
    enabled,
    onBookingUpdate: (booking: BookingTable) => {
      console.log(`[BookingSync] Booking ${booking.bookingId} updated, invalidating queries...`)

      // Invalider les queries pour déclencher un refetch automatique
      queryClient.invalidateQueries({ queryKey: ['booking'] })

      // Optionnel: update optimiste du cache avant le refetch
      queryClient.setQueryData(['booking'], (old: any) => {
        if (!Array.isArray(old)) return old

        const bookings = old as BookingTable[]
        const index = bookings.findIndex((b) => b.bookingId === booking.bookingId)

        if (index !== -1) {
          // Mise à jour d'un booking existant
          const updatedBookings = [...bookings]
          updatedBookings[index] = booking
          return updatedBookings
        } else if (booking.shopId === shopId) {
          // Nouveau booking pour ce shop
          return [booking, ...bookings]
        }

        return old
      })
    },
    onError: (error) => {
      console.error('[BookingSync] Subscription error:', error)
    },
  })

  return {
    /** Dernier booking reçu via subscription */
    lastBooking,
    /** État de connexion à AppSync */
    isConnected,
    /** Erreur de subscription */
    error,
  }
}
